import { Quaternion, Vector3, CatmullRomCurve3 } from "three";
// stores the transformation from the client and interpolates between two known timestamps or to the last known information.
export class TransformHistory {
    constructor() {
        this.states = [];
        this.startPos = 0;
        this.endPos = 0;
        this.maxSize = 10;
        this.catmullRomCurve3Positions = [];
        for (let i = 0; i < 4; i++) {
            this.catmullRomCurve3Positions.push(new Vector3());
        }
        this.catmullRomCurve3 = new CatmullRomCurve3(this.catmullRomCurve3Positions);
        this.catmullRomCurve3.type = "centripetal";
    }
    /** Returns an unused State from this.states  */
    getFreeState() {
        if (this.endPos === this.states.length) {
            this.states.push({
                timestamp: 0,
                position: new Vector3(),
                scale: new Vector3(),
                rotation: new Quaternion()
            });
        }
        return this.states[this.endPos];
    }
    addState(state) {
        if (this.startPos !== this.endPos) {
            if (state.timestamp < this.states[this.getPrevPos(this.endPos)].timestamp)
                return;
        }
        if (this.endPos >= this.states.length) {
            this.states.push(state);
        }
        else {
            this.states[this.endPos] = state;
        }
        this.endPos = this.getNextPos(this.endPos);
        if (this.endPos === this.startPos) {
            this.startPos = this.getNextPos(this.endPos);
        }
    }
    interpolate(time, delay, pos, scale, rot) {
        if (this.startPos === this.endPos)
            return false;
        const currTime = time - delay;
        let index = this.getPrevPos(this.endPos);
        const firstState = this.states[index];
        if (firstState.timestamp < currTime) {
            pos.copy(firstState.position);
            scale.copy(firstState.scale);
            rot.copy(firstState.rotation);
            return true;
        }
        while (index !== this.startPos) {
            const state = this.states[index];
            const prevState = this.states[this.getPrevPos(index)];
            if (state.timestamp >= currTime && prevState.timestamp <= currTime) {
                const delta = state.timestamp - prevState.timestamp;
                const lerp = (currTime - prevState.timestamp) / delta;
                const prevPrevState = this.states[this.getPrevPos(this.getPrevPos(index))];
                this.catmullRomCurve3Positions[0].copy(prevPrevState.position);
                this.catmullRomCurve3Positions[1].copy(prevState.position);
                this.catmullRomCurve3Positions[2].copy(state.position);
                const nextPos = this.getNextPos(index);
                if (nextPos !== this.endPos && nextPos < this.states.length) {
                    const nextState = this.states[nextPos];
                    this.catmullRomCurve3Positions[3].copy(nextState.position);
                }
                else {
                    this.catmullRomCurve3Positions[3].copy(state.position);
                }
                const p = 1 / 3;
                pos.copy(this.catmullRomCurve3.getPoint(p + p * lerp, pos));
                scale.lerpVectors(prevState.scale, state.scale, lerp);
                rot.copy(prevState.rotation).slerp(state.rotation, lerp);
                return true;
            }
            index = this.getPrevPos(index);
        }
        const lastState = this.states[this.startPos];
        pos.copy(lastState.position);
        scale.copy(lastState.scale);
        rot.copy(lastState.rotation);
        return true;
    }
    getPrevPos(pos) {
        let newPos = pos - 1;
        if (newPos < 0)
            newPos = this.states.length - 1;
        return newPos;
    }
    getNextPos(pos) {
        return (pos + 1) % this.maxSize;
    }
}
